/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { InlineNotification, Link } from '@coursera/cds-core';

import type { UserFilter } from 'bundles/enterprise-admin-user-filters/types';

import _t from 'i18n!nls/enterprise-admin-user-filters';

export type UserFilterBuilderErrors = {
  numberOfDaysInactive?: string;
  numberOfDaysSinceCompletion?: string;
  programJoinDateFromAfterTo?: string;
};

export type UserFilterBuilderError = keyof UserFilterBuilderErrors;

export const validateUserFilter = (userFilter: UserFilter): UserFilterBuilderErrors => {
  const errors: UserFilterBuilderErrors = {};

  const programJoinDate =
    userFilter.primaryFilter?.type === 'MEMBER' ? userFilter.primaryFilter?.programJoinDate : undefined;
  if (
    programJoinDate &&
    programJoinDate.type === 'ABSOLUTE' &&
    programJoinDate.after &&
    programJoinDate.before &&
    programJoinDate.after > programJoinDate.before
  ) {
    errors.programJoinDateFromAfterTo = _t('‘From date’ cannot be after the ‘To date’');
  }

  if (programJoinDate && programJoinDate.type === 'ABSOLUTE' && !programJoinDate.after) {
    errors.programJoinDateFromAfterTo = _t('Valid ’From date’ is required for this date filter');
  }
  const activityFilter =
    userFilter.primaryFilter?.type === 'MEMBER' ? userFilter.primaryFilter?.activityFilter : undefined;
  const type = activityFilter?.type;
  if (
    (type === 'GENERAL_INACTIVITY' || type === 'ENROLLED_BUT_INACTIVE') &&
    activityFilter?.lastActivityDate?.type === 'RELATIVE' &&
    !activityFilter?.lastActivityDate?.nDaysAgoBefore
  ) {
    errors.numberOfDaysInactive = _t('Enter a number greater than or equal to 1');
  }
  if (
    type === 'COMPLETED' &&
    activityFilter?.completionDate?.type === 'RELATIVE' &&
    !activityFilter?.completionDate?.nDaysAgoAfter
  ) {
    errors.numberOfDaysSinceCompletion = _t('Enter a number greater than or equal to 1');
  }
  return errors;
};

const ErrorsNotification = ({ errors, ...props }: { errors: UserFilterBuilderErrors }) => {
  return (
    <InlineNotification
      severity="error"
      title={_t('Filters cannot be applied, there is 1 error')}
      data-testid="user-filter-builder-errors"
      {...props}
    >
      <React.Fragment>
        {errors.programJoinDateFromAfterTo && (
          <Link href="#programJoinDateFromAfterTo">
            {_t('Primary filter, #{error}', { error: errors.programJoinDateFromAfterTo })}
          </Link>
        )}
        {errors.numberOfDaysInactive && (
          <Link href="#numberOfDaysInactive">
            {_t(
              'Activity filter, ‘Inactive for last (days)’ is not a valid number, specify a number greater than or equal to 1'
            )}
          </Link>
        )}
        {errors.numberOfDaysSinceCompletion && (
          <Link href="#numberOfDaysSinceCompletion">
            {_t(
              'Activity filter, ‘Last completion date within (days)’ is not a valid number, specify a number greater than or equal to 1'
            )}
          </Link>
        )}
      </React.Fragment>
    </InlineNotification>
  );
};

export default ErrorsNotification;
